import { useMemo, useState } from "react";
import ProductFormModal from "@/pages/admin/products/components/ProductFormModal";
import CatalogManagerModal from "@/pages/admin/products/components/CatalogManagerModal";
import { useCatalogData } from "@/hooks/useCatalog";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Product } from "@/types";

const currency = new Intl.NumberFormat("es-DO", {
  style: "currency",
  currency: "DOP",
  minimumFractionDigits: 2,
});

function formatMoney(value: number | null | undefined): string {
  return currency.format(Number(value ?? 0));
}

export default function AdminProductsPage() {
  const toast = useToast();
  const { profile, hasPermission } = useAuth();
  const canSeeCost = hasPermission("cost.view");
  const { products, categories, brands, stockByProduct, loading, error, reload } = useCatalogData();
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const publicCatalogPath = `${basePath ? `/${basePath}` : ""}/catalogo`;

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((product) => {
      if (categoryFilter && product.category_id !== categoryFilter) return false;
      if (statusFilter === "active" && !product.active) return false;
      if (statusFilter === "inactive" && product.active) return false;
      if (statusFilter === "catalog" && !product.show_in_catalog) return false;
      if (statusFilter === "low") {
        const stock = stockByProduct[product.id];
        if (!stock || !stock.low) return false;
      }
      if (!term) return true;
      const haystack = `${product.name} ${product.sku ?? ""} ${product.barcode ?? ""} ${
        product.brand_name ?? ""
      } ${product.tire_size ?? ""}`.toLowerCase();
      return haystack.includes(term);
    });
  }, [products, stockByProduct, search, categoryFilter, statusFilter]);

  const stats = useMemo(() => {
    const active = products.filter((product) => product.active).length;
    const inCatalog = products.filter((product) => product.show_in_catalog).length;
    const low = products.filter((product) => stockByProduct[product.id]?.low).length;
    return { total: products.length, active, inCatalog, low };
  }, [products, stockByProduct]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (product: Product) => {
    setEditing(product);
    setFormOpen(true);
  };

  const handleToggleActive = async (product: Product) => {
    setTogglingId(product.id);
    const { error: updateError } = await supabase
      .from("products")
      .update({ active: !product.active, updated_at: new Date().toISOString() })
      .eq("id", product.id);
    setTogglingId(null);
    if (updateError) {
      toast.error("No se pudo actualizar el estado del producto.");
      return;
    }
    await logAudit({
      userId: profile?.id ?? null,
      userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
      action: "editar",
      entity: "products",
      entityId: product.id,
      oldValue: { active: product.active },
      newValue: { active: !product.active },
    });
    toast.success(product.active ? "Producto desactivado." : "Producto activado.");
    reload();
  };

  const statCards = [
    { key: "total", label: "Productos", value: stats.total, icon: "ri-shopping-bag-3-line", tone: "primary" },
    { key: "active", label: "Activos", value: stats.active, icon: "ri-checkbox-circle-line", tone: "accent" },
    { key: "catalog", label: "En catálogo", value: stats.inCatalog, icon: "ri-store-3-line", tone: "secondary" },
    { key: "low", label: "Bajo stock", value: stats.low, icon: "ri-alert-line", tone: "accent" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-800",
    secondary: "bg-secondary-200 text-secondary-900",
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950">Productos</h1>
          <p className="mt-1.5 text-sm text-foreground-500">
            Administra el catálogo de productos, precios regular/mayorista, medidas y compatibilidad.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a
            href={publicCatalogPath}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-node-tree text-base"></i>
            Mapa del catálogo
          </a>
          <button
            type="button"
            onClick={() => setManagerOpen(true)}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-price-tag-3-line text-base"></i>
            Categorías y marcas
          </button>
          <button
            type="button"
            onClick={openCreate}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-add-line text-base"></i>
            Nuevo producto
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {statCards.map((card) => (
          <div
            key={card.key}
            className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3"
          >
            <span className={`flex h-9 w-9 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
              <i className={`${card.icon} text-base`}></i>
            </span>
            <div>
              <p className="font-heading text-lg font-bold leading-none text-foreground-950">
                {card.value}
              </p>
              <p className="mt-1 text-[11px] text-foreground-500">{card.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 lg:flex-row lg:items-center">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
            <i className="ri-search-line text-base"></i>
          </span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nombre, SKU, código de barras, marca o medida..."
            className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="">Todas las categorías</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
            <option value="catalog">En catálogo</option>
            <option value="low">Bajo stock</option>
          </select>
        </div>
      </div>

      {error ? (
        <div className="flex flex-col items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm text-primary-800 sm:flex-row sm:items-center sm:justify-between">
          <span className="flex items-center gap-2">
            <i className="ri-error-warning-line text-base"></i>
            {error}
          </span>
          <button
            type="button"
            onClick={reload}
            className="whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700"
          >
            Reintentar
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg"></i>
          Cargando productos...
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-100 px-4 py-14 text-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-shopping-bag-3-line text-2xl"></i>
          </span>
          <p className="text-sm font-semibold text-foreground-800">Sin productos</p>
          <p className="max-w-sm text-xs text-foreground-500">
            Crea tu primer producto. Se agregará automáticamente al inventario de cada sucursal.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-lg border border-background-200 lg:block">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-background-100 text-left text-[11px] font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-4 py-3">Producto</th>
                  <th className="px-4 py-3">Categoría / Marca</th>
                  <th className="px-4 py-3 text-right">Precio</th>
                  <th className="px-4 py-3 text-right">Mayorista</th>
                  {canSeeCost ? <th className="px-4 py-3 text-right">Costo</th> : null}
                  <th className="px-4 py-3 text-right">Stock</th>
                  <th className="px-4 py-3">Estado</th>
                  <th className="px-4 py-3 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200 bg-background-50">
                {filtered.map((product) => {
                  const stock = stockByProduct[product.id];
                  return (
                    <tr key={product.id} className="text-sm text-foreground-800">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-background-200 bg-background-100">
                            {product.image_url ? (
                              <img src={product.image_url} alt={product.name} className="h-full w-full object-cover" />
                            ) : (
                              <i className="ri-image-line text-foreground-400"></i>
                            )}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-foreground-950">{product.name}</p>
                            <p className="truncate text-[11px] text-foreground-400">
                              {product.sku ? `SKU: ${product.sku}` : "Sin SKU"}
                              {product.tire_size ? ` · ${product.tire_size}` : ""}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs text-foreground-600">{product.category_name ?? "—"}</p>
                        <p className="text-[11px] text-foreground-400">{product.brand_name ?? "Sin marca"}</p>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground-950">
                        {formatMoney(product.price)}
                      </td>
                      <td className="px-4 py-3 text-right text-foreground-600">
                        {formatMoney(product.wholesale_price)}
                      </td>
                      {canSeeCost ? (
                        <td className="px-4 py-3 text-right text-foreground-600">
                          {formatMoney(product.cost_price)}
                        </td>
                      ) : null}
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            stock?.low
                              ? "bg-primary-100 text-primary-700"
                              : "bg-background-200 text-foreground-700"
                          }`}
                        >
                          {stock?.total ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${
                            product.active ? "bg-accent-100 text-accent-900" : "bg-secondary-200 text-secondary-900"
                          }`}
                        >
                          {product.active ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEdit(product)}
                            className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600 transition-colors hover:bg-background-100"
                          >
                            <i className="ri-edit-2-line text-sm"></i>
                          </button>
                          <button
                            type="button"
                            disabled={togglingId === product.id}
                            onClick={() => handleToggleActive(product)}
                            className={`flex h-8 w-8 items-center justify-center rounded-md border transition-colors disabled:opacity-60 ${
                              product.active
                                ? "border-primary-200 bg-primary-50 text-primary-700"
                                : "border-accent-300 bg-accent-50 text-accent-900"
                            }`}
                          >
                            <i
                              className={`${
                                product.active ? "ri-close-circle-line" : "ri-checkbox-circle-line"
                              } text-sm`}
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
            {filtered.map((product) => {
              const stock = stockByProduct[product.id];
              return (
                <div
                  key={product.id}
                  className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-50">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="h-full w-full object-cover" />
                      ) : (
                        <i className="ri-image-line text-xl text-foreground-400"></i>
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground-950">{product.name}</p>
                      <p className="truncate text-[11px] text-foreground-400">
                        {product.category_name ?? "Sin categoría"} · {product.brand_name ?? "Sin marca"}
                      </p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-semibold text-secondary-900">
                          {stock?.total ?? 0} en stock
                        </span>
                        {stock?.low ? (
                          <span className="rounded-full bg-primary-100 px-2 py-0.5 text-[10px] font-semibold text-primary-700">
                            Bajo stock
                          </span>
                        ) : null}
                        {!product.active ? (
                          <span className="rounded-full bg-secondary-200 px-2 py-0.5 text-[10px] font-semibold text-secondary-900">
                            Inactivo
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 border-t border-background-200 pt-3">
                    <div>
                      <p className="font-heading text-base font-bold text-foreground-950">
                        {formatMoney(product.price)}
                      </p>
                      <p className="text-[11px] text-foreground-400">
                        Mayorista: {formatMoney(product.wholesale_price)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(product)}
                        className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 bg-background-50 text-foreground-600"
                      >
                        <i className="ri-edit-2-line text-base"></i>
                      </button>
                      <button
                        type="button"
                        disabled={togglingId === product.id}
                        onClick={() => handleToggleActive(product)}
                        className={`flex h-9 w-9 items-center justify-center rounded-md border disabled:opacity-60 ${
                          product.active
                            ? "border-primary-200 bg-primary-50 text-primary-700"
                            : "border-accent-300 bg-accent-50 text-accent-900"
                        }`}
                      >
                        <i
                          className={`${product.active ? "ri-close-circle-line" : "ri-checkbox-circle-line"} text-base`}
                        ></i>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <ProductFormModal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={reload}
        product={editing}
        categories={categories.filter((category) => category.active)}
        brands={brands.filter((brand) => brand.active)}
      />

      <CatalogManagerModal
        open={managerOpen}
        onClose={() => setManagerOpen(false)}
        onChanged={reload}
        categories={categories}
        brands={brands}
      />
    </div>
  );
}